import { useState, useRef, useEffect } from "react";
import classNames from "classnames";

const SelectField = ({
  label,
  id,
  value,
  onChange,
  options = [],
  placeholder = "Select an option",
  error,
  serverError,
  disabled = false,
  size = "sm",
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const displayRef = useRef(null);
  const [dropdownWidth, setDropdownWidth] = useState(0);

  const fieldName = id || props.name;

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find((opt) => opt.value === value);

  const handleSelect = (val) => {
    onChange({ target: { name: fieldName, value: val } });
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClickOutside = (e) => {
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Measure and set dropdown width on open
  useEffect(() => {
    if (isOpen && displayRef.current) {
      setDropdownWidth(displayRef.current.offsetWidth);
    }
  }, [isOpen]);

  const sizeClasses = classNames({
    "px-3 py-2 text-[12px]": size === "sm",
    "px-4 py-3 text-sm": size === "md",
    "px-5 py-4 text-base": size === "lg",
  });

  const fieldClasses = classNames(
    "border w-full bg-white rounded-lg cursor-pointer transition-all duration-300 outline-none",
    sizeClasses,
    {
      "border-gray-300": !error && !serverError && !disabled,
      "focus:border-primary-500": !error && !serverError && !disabled,
      "border-red-500": error || serverError,
      "bg-gray-100 cursor-not-allowed": disabled,
    }
  );

  return (
    <div className="relative w-full" ref={containerRef}>
      {label && (
        <label
          htmlFor={id}
          className="block mb-1 ml-1 text-sm font-medium text-gray-700"
        >
          {label}
        </label>
      )}

      <div
        className={fieldClasses}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        ref={displayRef}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex-1 min-w-0 overflow-hidden">
            <span className="block truncate whitespace-nowrap text-ellipsis">
              {selectedOption?.label || placeholder}
            </span>
          </div>
          <span className="flex-shrink-0 pl-2 text-sm text-gray-400">&#9662;</span>
        </div>
      </div>

      {isOpen && !disabled && (
        <div 
          className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-lg max-h-60"
          style={{ width: dropdownWidth }}
        >
          <input
            type="text"
            className="w-full px-3 py-2 text-sm border-b border-gray-200 focus:outline-none"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                className="px-3 py-2 text-sm truncate cursor-pointer hover:bg-gray-100"
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-400">No options</div>
          )}
        </div>
      )}

      {(error || serverError) && (
        <div className="text-[12px] text-red-500 mt-1 ml-2">
          {error || serverError}
        </div>
      )}
    </div>
  );
};

export default SelectField;